<script setup lang="ts">
import { reactive, ref, nextTick, computed } from 'vue';
import { useForm, Field, ErrorMessage } from 'vee-validate';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import { MEDICARE_APPOINTMENT_SCOPES } from '@/consent/medicare/domain/MedicareAppointmentOptions';
import { useMedicareConsentStore } from '@/consent/medicare/infrastructure/composition/useMedicareConsentStore';

const { medicareConsentStore, medicareConsentState } = useMedicareConsentStore();

const { handleSubmit, validate } = useForm();
const runValidations = handleSubmit(() => true);

const autoFillEnabled = ref(true);
const selectAll = ref(false);
const selectAllInitials = ref('');

const getInitialsByKey = (key: MEDICARE_APPOINTMENT_SCOPES): string => medicareConsentState.value.application.appointment_scopes.find(o => o.key === key)?.initials || '';

const sectionTitle = computed(() => medicareConsentState.value.application.appointment_required ? 'Schedule an Appointment' : 'Consent information');

const selectedConsultationOptions = ref(medicareConsentState.value.application.appointment_scopes.map((scope) => scope.key) as Array<string>);
const medicareConsultationOptions: Array<{ text: string, value: MEDICARE_APPOINTMENT_SCOPES, initials: string }> = reactive([{
  text: 'Stand-alone Medicare Prescription Drug Plans (Part D)',
  value: MEDICARE_APPOINTMENT_SCOPES.MEDICARE_PART_D,
  initials: getInitialsByKey(MEDICARE_APPOINTMENT_SCOPES.MEDICARE_PART_D)
}, {
  text: 'Medicare Advantage Plans (Part C) and Cost Plans',
  value: MEDICARE_APPOINTMENT_SCOPES.MEDICARE_PART_C,
  initials: getInitialsByKey(MEDICARE_APPOINTMENT_SCOPES.MEDICARE_PART_C)
}, {
  text: 'Dental/Vision/Hearing Products ',
  value: MEDICARE_APPOINTMENT_SCOPES.DENTAL_VISION_HEARING,
  initials: getInitialsByKey(MEDICARE_APPOINTMENT_SCOPES.DENTAL_VISION_HEARING)
}, {
  text: 'Hospital Indemnity Products',
  value: MEDICARE_APPOINTMENT_SCOPES.HOSPITAL_PRODUCTS,
  initials: getInitialsByKey(MEDICARE_APPOINTMENT_SCOPES.HOSPITAL_PRODUCTS)
}, {
  text: 'Medicare Supplement Plans (Medigap) Products',
  value: MEDICARE_APPOINTMENT_SCOPES.SUPPLEMENT_PRODUCTS,
  initials: getInitialsByKey(MEDICARE_APPOINTMENT_SCOPES.SUPPLEMENT_PRODUCTS)
}]);

const sectionSubmit = async (): Promise<boolean | undefined> => {
  const localValid = await runValidations();

  if (localValid) {
    updateForm();
  }

  return localValid;
};

const selectAllChange = (): void => {
  selectedConsultationOptions.value = [];
  autoFillEnabled.value = false;

  if (selectAll.value) {
    medicareConsultationOptions.forEach(option => {
      selectedConsultationOptions.value.push(option.value);
    });
  }
  nextTick(() => {
    autoFillEnabled.value = true;
  });
};

const selectAllInitialsChange = (): void => {
  medicareConsultationOptions.forEach(option => {
    option.initials = selectAllInitials.value;
  });
};

const updateForm = (): void => {
  const consultationOptions = medicareConsultationOptions.filter(o => o.initials).map((option) => {
    return {
      initials: option.initials,
      key: option.value
    };
  });
  medicareConsentStore.updateForm({ appointment_scopes: consultationOptions });
};

defineExpose({
  sectionSubmit: sectionSubmit,
  sectionIsValid: async (): Promise<boolean> => {
    const result = await validate({ mode: 'silent' });
    if (result.valid) {
      updateForm();
    }

    return result.valid;
  }
});
</script>
<template>
  <BaseSection :title="sectionTitle">
    <div class="flex flex-col gap-4 mb-5">
      <div class="mb-2 text-blue-500">
        <div class="sm:flex-1 mr-2 mt-2"><span class="text-blue-500 font-medium text-lg">What would you like to discuss during your appointment?</span></div>
        <p>Please check the boxes for all plan types you would like to discuss. If you are not sure, click "Select All." Anything you do not check CANNOT be discussed without filling out another form. Please note that checking these boxes does not affect your current enrollment or obligate you to enroll in a plan.</p>
      </div>
      <div class="flex flex-col sm:flex-row sm:h-6">
        <div class="flex sm:w-[440px] items-center">
          <label class="flex items-center">
            <input type="checkbox" :value="true" v-model="selectAll" @change="selectAllChange" class="form-checkbox h-5 w-5 text-indigo-600 transition duration-150 ease-in-out" />
            <span class="ml-2">Select All</span>
          </label>
        </div>
        <div class="flex items-center" v-if="selectAll">
          <div class="h-5 flex items-center mr-2 pt-5 pb-2">
            <span class="whitespace-nowrap ml-7 sm:ml-0">Add initials here</span>
            <Field v-model="selectAllInitials" type="text" v-slot="{ field, meta }" :name="`initialsAll`" rules="alpha_spaces|min:2|max:5">
              <input v-autofocus="true" v-bind="field" @change="selectAllInitialsChange" v-upperCase="field" type="text" autocomplete="new-password" :class="{ ...meta }" class="py-0 px-2 m-0 h-auto w-14 rounded-md ml-2 self-padding">
            </Field>
          </div>
        </div>
      </div>
      <div class="flex flex-col sm:flex-row sm:h-6" v-for="(consultationOption, i) in medicareConsultationOptions" :key="i">
        <div class="flex sm:w-[440px] items-start sm:items-center">
          <Field name="consultationOption" :id="`consultationOption${i}`" v-model="selectedConsultationOptions" type="checkbox" :value="consultationOption.value" rules="required" class="form-checkbox h-5 w-5 text-indigo-600 transition duration-150 ease-in-out focus:ring-blue-500 focus:ring-2 mr-1 mt-0.5 sm:mt-0" />
          <label :for="`consultationOption${i}`" class="flex items-center ml-1 max-w-[calc(100%-40px)]"> {{ consultationOption.text }} </label>
        </div>
        <div class="flex items-center" v-if="selectedConsultationOptions.includes(consultationOption.value)">
          <div class="h-5 flex items-center mr-2 py-5">
            <span class="whitespace-nowrap ml-7 sm:ml-0">Add initials here</span>
            <Field v-model="consultationOption.initials" type="text" v-slot="{ field, errorMessage, meta }" :name="`initials${i}`" rules="required|alpha_spaces|min:2|max:5">
              <input v-autofocus="autoFillEnabled" v-bind="field" v-upperCase="field" type="text" autocomplete="new-password" :class="{ ...meta }" class="py-0 px-2 m-0 h-auto w-14 rounded-md ml-2 self-padding">
              <span class="error ml-2">{{ errorMessage }}</span>
            </Field>
          </div>
        </div>
      </div>
      <ErrorMessage name="consultationOption" class="error" />
    </div>
  </BaseSection>
</template>
